var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

import React, { useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import nanoidGenerate from "nanoid/generate";
import nanoidDictionary from "nanoid-dictionary/lowercase";
import { get, set, startsWith, trim, isNil, isNumber, fill, isPlainObject, trimStart, compact } from "lodash-es";
import numeral from "numeral";
import { tokenize } from "./parser";
import { NODE_NAME_MAX_LENGTH, FORMULA_COMPLETION_TYPES, REQUEST_STATES } from "./common";

export var generateId = function generateId() {
  var length = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 5;
  return nanoidGenerate(nanoidDictionary, length);
};

export var getNewNodeName = function getNewNodeName() {
  var nodes = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "name_";

  var count = 0;
  var prefixLength = prefix.length;

  nodes.forEach(function (_ref) {
    var _ref$name = _ref.name,
        name = _ref$name === undefined ? "" : _ref$name;

    if (name.length > prefixLength && prefix === name.substring(0, prefixLength).toLowerCase()) {
      var suffix = name.substring(prefixLength, name.length);
      !isNaN(suffix) && +suffix >= count && (count = +suffix);
    }
  });

  return prefix + (count + 1);
};

export var getNewFormulaName = function getNewFormulaName(nodes) {
  var prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "formula";
  return getNewNodeName(nodes, prefix);
};

export var getNewTableName = function getNewTableName(nodes) {
  var prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "table";
  return getNewNodeName(nodes, prefix);
};

// ToDo: optimise; function below is almost the same as getNewNodeName
export var getNewColumnName = function getNewColumnName(_ref2) {
  var columns = _ref2.columns;

  var count = 0;
  var prefix = "Column";
  var prefixLength = prefix.length;

  columns.forEach(function (_ref3) {
    var name = _ref3.name;

    if (name.length > prefixLength && prefix.toLowerCase() === name.substring(0, prefixLength).toLowerCase()) {
      var suffix = name.substring(prefixLength, name.length);
      !isNaN(suffix) && +suffix >= count && (count = +suffix);
    }
  });

  return prefix + (count + 1);
};

export var isFormula = function isFormula(text) {
  return typeof text !== "string" || !text ? false : startsWith(text.trim(), "=");
};

export var getFormattedValue = function getFormattedValue(value, formatter) {
  if (isNil(value) || value === "") {
    return "";
  }

  if (value instanceof Date) {
    // ToDo: handle dates better
    return value.toDateString();
  }

  if (isNil(formatter)) {
    return value;
  }

  return numeral(value).format(formatter);
};

export var standardizeInput = function standardizeInput(input) {
  if (!input) {
    return input;
  }

  if (isNaN(input)) {
    return trim(input);
  }

  return parseFloat(input);
};

export var prependWithFormulaSign = function prependWithFormulaSign() {
  var text = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
  var spaceAfter = arguments[1];

  return "=" + (spaceAfter || "") + text;
};

export var getFormulaHints = function getFormulaHints() {
  var formulas = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var id = arguments[1];

  return formulas.filter(function (f) {
    return f.id !== id;
  }).map(function (_ref4) {
    var name = _ref4.name,
        formatted = _ref4.formatted;
    return {
      name: name,
      description: "value: " + formatted,
      type: FORMULA_COMPLETION_TYPES.formula
    };
  });
};

export var getTablesHints = function getTablesHints() {
  var tables = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];

  return tables.map(function (_ref5) {
    var name = _ref5.name;
    return {
      name: name,
      description: "Table name for " + name,
      type: FORMULA_COMPLETION_TYPES.table
    };
  });
};

export var pause = function pause() {
  return new Promise(function (resolve) {
    setTimeout(resolve);
  });
};

export var validateNodeName = function validateNodeName(name) {
  var maxLength = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : NODE_NAME_MAX_LENGTH;

  if (!name || typeof name !== "string" || name === "" || name.length > maxLength) {
    return false;
  }

  return (/^[a-zA-Z][a-zA-Z_\d]+$/.test(name)
  );
};

export var hasCircularDependency = function hasCircularDependency(name, expression) {
  return tokenize(expression).some(function (token) {
    return get(token, "name") === name;
  });
};

// ToDo: test if works for more complicated formulas like table1[[foo]:[bar]
export var hasCircularDependencyTableColumn = function hasCircularDependencyTableColumn(name, expression, tableName) {
  return tokenize(expression).filter(function (t) {
    return isPlainObject(t) && t.type === "table" && t.name === tableName;
  }).some(function (_ref6) {
    var _ref6$intraTableRefer = _ref6.intraTableReference,
        intraTableReference = _ref6$intraTableRefer === undefined ? [] : _ref6$intraTableRefer;
    return intraTableReference.some(function (t2) {
      return isPlainObject(t2) && t2.type === "columnRange" && t2.columns.some(function (c) {
        return c.name === name;
      });
    });
  });
};

export var mapColumnsToCells = function mapColumnsToCells() {
  var columns = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var value = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  return columns.map(function (_ref7) {
    var id = _ref7.id;
    return _extends({
      column: id
    }, value);
  });
};

export var createAndFillGrid = function createAndFillGrid() {
  var rowCount = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 4;
  var columns = arguments[1];
  var value = arguments[2];

  return fill(Array(rowCount), { cells: mapColumnsToCells(columns, value) });
};

export var reorderArrayElements = function reorderArrayElements(_array, oldIndex, newIndex) {
  var length = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 1;

  var array = _array.slice(); // in order to not mutate original array
  var slice = array.splice(oldIndex, length);
  array.splice.apply(array, [newIndex, 0].concat(slice));
  return array;
};

var renderPlainTable = function renderPlainTable(table) {
  var _extends2;

  var _ref8 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref8$dataTypeAttrNam = _ref8.dataTypeAttrName,
      dataTypeAttrName = _ref8$dataTypeAttrNam === undefined ? "data-reactivepad-type" : _ref8$dataTypeAttrNam,
      _ref8$dataPropsAttrNa = _ref8.dataPropsAttrName,
      dataPropsAttrName = _ref8$dataPropsAttrNa === undefined ? "data-reactivepad-props" : _ref8$dataPropsAttrNa,
      _ref8$showCaption = _ref8.showCaption,
      showCaption = _ref8$showCaption === undefined ? true : _ref8$showCaption;

  var columns = table.columns,
      rows = table.rows,
      tableProps = _objectWithoutProperties(table, ["columns", "rows"]);

  var createPropsAttribute = function createPropsAttribute(props) {
    var _ref9;

    return _ref9 = {}, _ref9[dataPropsAttrName] = JSON.stringify(props), _ref9;
  };

  return React.createElement(
    "table",
    _extends((_extends2 = {}, _extends2[dataTypeAttrName] = tableProps.type, _extends2), createPropsAttribute(tableProps)),
    showCaption && React.createElement(
      "caption",
      null,
      tableProps.name
    ),
    React.createElement(
      "thead",
      null,
      React.createElement(
        "tr",
        null,
        columns.map(function (col) {
          return React.createElement(
            "th",
            _extends({
              key: col.id,
              style: { width: col.width }
            }, createPropsAttribute(col)),
            col.name
          );
        })
      )
    ),
    React.createElement(
      "tbody",
      null,
      rows.map(function (row) {
        var cells = row.cells,
            rowProps = _objectWithoutProperties(row, ["cells"]);

        return React.createElement(
          "tr",
          _extends({
            key: row.id,
            style: { height: row.height }
          }, createPropsAttribute(rowProps)),
          cells.map(function (cell) {
            return React.createElement(
              "td",
              _extends({
                key: cell.id
              }, createPropsAttribute(cell)),
              cell.formatted
            );
          })
        );
      })
    ),
    tableProps.showFooter && React.createElement(
      "tfoot",
      null,
      columns.map(function (_ref10) {
        var id = _ref10.id,
            footerFormatted = _ref10.footerFormatted;
        return React.createElement(
          "td",
          { key: id },
          footerFormatted
        );
      })
    )
  );
};

export { renderPlainTable };
export var renderPlainTableAsStatic = function renderPlainTableAsStatic() {
  return renderToStaticMarkup(renderPlainTable.apply(undefined, arguments));
};

export var renderPlainTableAsDOM = function renderPlainTableAsDOM() {
  return stringToHTML(renderToStaticMarkup(renderPlainTable.apply(undefined, arguments)));
};

var domParserSupported = function () {
  if (!window.DOMParser) return false;
  var parser = new DOMParser();
  try {
    parser.parseFromString("x", "text/html");
  } catch (err) {
    return false;
  }
  return true;
}();

export function stringToHTML(str) {
  if (domParserSupported) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(str, "text/html");
    return doc.body;
  }

  var dom = document.createElement("div");
  dom.innerHTML = str;
  return dom;
}

export function parseSheetUrl(url) {
  if (!url) {
    return null;
  }

  var _compact = compact(trimStart(url, "https://").split("#")),
      coreUrl = _compact[0],
      gid = _compact[1];

  var sheetId = gid && get(gid.split("="), "[1]");
  var spreadId = coreUrl && get(coreUrl.split("/"), "[3]");

  return {
    spreadId: spreadId,
    sheetId: sheetId
  };
}

var _fn = function _fn() {
  return null;
};

export function handleRequestCases() {
  var cases = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var requestState = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var _cases$pending = cases.pending,
      pending = _cases$pending === undefined ? _fn : _cases$pending,
      _cases$fulfilled = cases.fulfilled,
      fulfilled = _cases$fulfilled === undefined ? _fn : _cases$fulfilled,
      _cases$rejected = cases.rejected,
      rejected = _cases$rejected === undefined ? _fn : _cases$rejected,
      _cases$none = cases.none,
      none = _cases$none === undefined ? _fn : _cases$none;


  switch (requestState) {
    case REQUEST_STATES.pending:
      {
        return pending();
      }
    case REQUEST_STATES.fulfilled:
      {
        return fulfilled();
      }
    case REQUEST_STATES.rejected:
      {
        return rejected();
      }
    default:
      {
        return none();
      }
  }
}

export function useRequestStateHandler() {
  var init = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;

  var _useState = useState(init),
      requestState = _useState[0],
      setRequestState = _useState[1];

  var _handleRequestCases = function _handleRequestCases(cases) {
    return handleRequestCases(cases, requestState);
  };

  return [requestState, setRequestState, _handleRequestCases];
}

export var adjustDuplicates = function adjustDuplicates() {
  var arr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var path = arguments[1];
  var adjuster = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function (name, i) {
    return name + " " + (i + 1);
  };
  var adjustFirst = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;

  return [].concat(arr).map(function (el, i) {
    var getVal = function getVal(_el) {
      return path ? get(_el, path) : _el;
    };
    var searchedValue = getVal(el);
    var sameValueCount = arr.slice(0, i).filter(function (duplicate) {
      return getVal(duplicate) === searchedValue;
    }).length;

    var _adjustFirst = adjustFirst && arr.slice(i + 1, arr.length).some(function (__el) {
      return getVal(__el) === searchedValue;
    });

    if (sameValueCount > 0 || _adjustFirst) {
      var adjustedValue = adjuster(searchedValue, sameValueCount);

      return path ? set(_extends({}, el), path, adjustedValue) : adjustedValue;
    }

    return el;
  });
};

export var getStepForNumber = function getStepForNumber(number) {
  if (isNil(number) || !isNumber(number)) {
    return null;
  }

  var _$split = ("" + number).split("."),
      decimal = _$split[0],
      fractional = _$split[1];

  if (!fractional) {
    return 1;
  }

  return parseFloat("0." + Array(fractional.length - 1).fill("0").join("") + "1");
};

export var isNumeric = function isNumeric(value) {
  return !isNaN(value);
};